import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, trainStat, setFlag } from '../core/save.js';
import { startQuest, completeQuest, isQuestStarted, isQuestComplete, isAvailable, isReady, turnIn, flagObjective } from '../core/quests.js';
import { addFound, hasFound } from '../core/collection.js';

// Forest Graveyard — a spooky clearing where the ghost of Moldy Moe
// paces among the tombstones, unable to rest without his lost burial crumb.

export function createGraveyardScene(){
  return () => new GraveyardScene();
}

class GraveyardScene {
  constructor(){
    this.panels = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x161A24);
    this.scene.fog = new THREE.FogExp2(0x161A24, 0.032);

    this.camera = new THREE.PerspectiveCamera(48, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 14;

    this._buildLights();
    this._buildGround();
    this._buildLamps();
    this._buildTombs();
    this._buildGhost();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 9);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: new THREE.Vector3(0, 0, -5.2), r: 2.4, label: '🗣 talk to Moldy Moe', action: () => this._talkGhost() },
      { pos: new THREE.Vector3(-4.2, 0, 2.0), r: 1.7, label: '✨ dig at the loose mound', action: () => this._digMound() },
      { pos: new THREE.Vector3(0, 0, 9.5), r: 2.2, label: '← back to the Forest', action: () => this._exit() }
    ];
    this.nearSpot = null;
    this.dug = false;
    this.questRewarded = false;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x3A3C50, 0.65));
    const moon = new THREE.DirectionalLight(0x8A9CB8, 0.6);
    moon.position.set(-5, 14, 7); moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    const s = 18;
    moon.shadow.camera.left = -s; moon.shadow.camera.right = s;
    moon.shadow.camera.top = s; moon.shadow.camera.bottom = -s;
    this.scene.add(moon);

    // eerie ghost glow
    const g = new THREE.PointLight(0x66AADD, 0.7, 12, 1.6);
    g.position.set(0, 3, -5); this.scene.add(g);
    this.ghostLight = g;
  }

  _buildGround(){
    // dark grass
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(28, 28), new THREE.MeshLambertMaterial({ color: 0x1B211A }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);

    // wrought-iron fence posts
    for (let x = -13; x <= 13; x += 2.6){
      [-13, 13].forEach(z => {
        cyl(0.04, 0.04, 2.4, 0x1A1A1A, x, 1.2, z, this.scene, 6);
        if (x < 13){
          const bar = cyl(0.02, 0.02, 2.6, 0x1A1A1A, x + 1.3, 1.2, z, this.scene, 6);
          bar.rotation.z = Math.PI/2;
        }
      });
    }
    for (let z = -13; z <= 13; z += 2.6){
      [-13, 13].forEach(x => {
        cyl(0.04, 0.04, 2.4, 0x1A1A1A, x, 1.2, z, this.scene, 6);
        if (z < 13){
          const bar = cyl(0.02, 0.02, 2.6, 0x1A1A1A, x, 1.2, z + 1.3, this.scene, 6);
          bar.rotation.x = Math.PI/2;
        }
      });
    }

    // dead trees
    const treePositions = [ [-10,-10], [11,-8], [-9,7], [10,11], [-12,0] ];
    treePositions.forEach(([x, z]) => {
      cyl(0.25, 0.32, 3.2, 0x2A2520, x, 1.6, z, this.scene, 8);
      cyl(0.05, 0.12, 1.6, 0x2A2520, x + 0.6, 3.4, z, this.scene, 5).rotation.z = -0.6;
      cyl(0.04, 0.1, 1.3, 0x2A2520, x - 0.5, 3.1, z + 0.3, this.scene, 5).rotation.z = 0.5;
    });
  }

  _buildLamps(){
    // wrought-iron lanterns around the yard — the graveyard is always dim, so
    // they stay lit to mark the path and keep the scene readable.
    const posts = [
      [-9, -9], [9, -9], [-9, 7], [9, 7],   // corners
      [-4.5, 6], [4.5, 6],                  // flanking the exit
      [0, -10]                               // behind the central tomb
    ];
    posts.forEach(([x, z]) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
      cyl(0.06, 0.08, 2.4, 0x1A1A1A, 0, 1.2, 0, g, 8);
      box(0.32, 0.06, 0.32, 0x1A1A1A, 0, 2.45, 0, g);
      const glass = box(0.22, 0.38, 0.22, 0xFFAA55, 0, 2.68, 0, g);
      glass.material = new THREE.MeshBasicMaterial({ color: 0xFFAA55 });
      const light = new THREE.PointLight(0xFFAA55, 1.2, 8, 1.6);
      light.position.set(0, 2.7, 0); g.add(light);
    });
  }

  _buildTombs(){
    // row of tombstones
    const stones = [
      [-5, -3], [-2.2, -4], [2.2, -4], [5, -3],
      [-6, 1], [-3.5, 0.5], [3.5, 0.5], [6, 1]
    ];
    stones.forEach(([x, z], i) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
      const grey = 0x4A4E55;
      box(0.7, 1.15, 0.18, grey, 0, 0.575, 0, g);
      box(0.5, 0.15, 0.22, grey, 0, 1.2, 0, g);
      // little moss patch
      sphere(0.12, 0x2F3A2F, -0.22, 0.12, 0.1, g, 8);
    });

    // big central tomb
    const big = new THREE.Group(); big.position.set(0, 0, -8); this.scene.add(big);
    box(2.6, 1.3, 1.6, 0x40444C, 0, 0.65, 0, big);
    box(2.2, 0.2, 1.4, 0x50565E, 0, 1.35, 0, big);

    // loose mound with crumb
    this.moundGroup = new THREE.Group(); this.moundGroup.position.set(-4.2, 0, 2.0); this.scene.add(this.moundGroup);
    const mound = sphere(0.65, 0x2A2520, 0, 0.35, 0, this.moundGroup, 12);
    mound.scale.set(1, 0.45, 1);
    mound.castShadow = true;
  }

  _buildGhost(){
    this.ghost = new THREE.Group(); this.ghost.position.set(0, 1.4, -5.2); this.scene.add(this.ghost);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x88CCFF,
      transparent: true,
      opacity: 0.6,
      roughness: 0.6,
      emissive: 0x224466,
      emissiveIntensity: 0.35
    });
    // body
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.42, 16, 16), mat);
    body.position.y = 0; body.scale.set(1, 1.15, 1); this.ghost.add(body);
    // tail
    const tail = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.9, 16, 1, true), mat);
    tail.position.y = -0.8; this.ghost.add(tail);
    // face
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 8), new THREE.MeshBasicMaterial({ color: 0x112233 }));
    eye.position.set(-0.12, 0.1, 0.34); this.ghost.add(eye);
    const eye2 = eye.clone(); eye2.position.set(0.12, 0.1, 0.34); this.ghost.add(eye2);
    this.ghostBaseY = 1.4;
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.35, 0.65];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this._positionCamera();
    HUD.setSubtitle('forest graveyard');
    HUD.say('The wind here smells faintly of stale croutons.');
    if (!isQuestStarted('moldy-moe')) startQuest('moldy-moe');
    if (isAvailable('moes-memory')) startQuest('moes-memory');

    const hasLantern = hasFound('ghost-lamp');
    this.bread.setLantern(hasLantern ? 0.9 : 0);
    if (hasLantern) HUD.say('Your ghost-lamp lights the way.');
  }

  exit(){
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _positionCamera(){
    this.azimuth = this.input.azimuthGoal ?? 0;
    this.polar   = this.input.polarGoal ?? 0.5;
    this.camera.position.set(
      this.bread.root.position.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      1.8 + this.camDist * Math.sin(this.polar),
      this.bread.root.position.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.bread.root.position.x, 1.6, this.bread.root.position.z);
  }

  _tap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-12, Math.min(12, gp.x));
    gp.z = Math.max(-12, Math.min(10, gp.z));
    this.bread.walkTo(gp);
  }

  _talkGhost(){
    this.bread.stop(); this.bread.drawFace('o');
    // Order matters here, and it is not obvious. 'moes-memory' is satisfied by the
    // flag that 'moldy-moe' raises when IT completes — so the moment moes-memory
    // is ready, moldy-moe is already complete. Checking the "thanks, little toast"
    // line first therefore shadows this turn-in forever, and the whole Baker King
    // arc dead-ends at Act II. Turn-ins before pleasantries.
    if (isQuestStarted('moes-memory') && isReady('moes-memory')){
      turnIn('moes-memory');
      HUD.refreshCrumbs();
      HUD.say("Moldy Moe: 'The keystone… yes, I remember now. It belongs in the Baker King's chamber, beyond the sealed gate.'");
      return;
    }
    if (isQuestComplete('moldy-moe')){
      HUD.say("Moldy Moe: 'Thanks, little toast. I can finally loaf around in peace.'");
      return;
    }
    // Returning the crumb. Gated on the saved objective rather than this.dug, so it
    // still works if you dig, wander off, and come back — and so that having
    // moes-memory in your log can't shadow it (it used to).
    if (isQuestStarted('moldy-moe') && isReady('moldy-moe') && !this.questRewarded){
      // complete quest
      this.questRewarded = true;
      const reward = completeQuest('moldy-moe') || {};
      HUD.refreshCrumbs();
      const gain = trainStat('zen', 1);
      if (gain > 0) HUD.refreshStats();
      const first = !hasFound('ghost-lamp');
      addFound('ghost-lamp');
      HUD.say("Moldy Moe: 'My crumb! You found it! Here, take this old ghost-lamp.'");
      this._panel(`
        <div class="cafe-card">
          <div class="cafe-grade">👻</div>
          <h2>Moe is at peace</h2>
          <p class="cafe-payout">🍞 +${reward.crumbs || 0} crumbs · +${gain} ZEN</p>
          <p class="fortress-loot">${first ? '🔦 Ghost-Lamp added to collection!' : 'another ghost-lamp flickers in your pack.'}</p>
          <button class="cafe-btn ghost close">rest easy</button>
        </div>
      `);
      this.panels[this.panels.length - 1].querySelector('.close').onclick = () => this._clearPanels();
      return;
    }
    if (isQuestStarted('moes-memory')){
      HUD.say("Moldy Moe: 'My memory is foggy… find my burial crumb first, and I will remember.'");
      return;
    }
    if (!isQuestStarted('moldy-moe')) startQuest('moldy-moe');
    HUD.say("Moldy Moe: 'I lost my burial crumb somewhere by the old tombstones… can you dig it up?'");
  }

  _digMound(){
    this.bread.stop(); this.bread.startPose('lift', 0.6);
    if (this.dug){
      HUD.say('Nothing but dirt left here.');
      return;
    }
    this.dug = true;
    this.moundGroup.visible = false;
    addCrumbs(5); HUD.refreshCrumbs();
    flagObjective('moldy-moe', 'moldyMoeCrumbFound');
    HUD.say('You dig up a faintly glowing crumb.');
    // visually leave a small hole
    const hole = new THREE.Mesh(new THREE.CircleGeometry(0.45, 16), new THREE.MeshLambertMaterial({ color: 0x151510 }));
    hole.rotation.x = -Math.PI/2; hole.position.set(-4.2, 0.04, 2.0); this.scene.add(hole);
  }

  _exit(){
    this.manager.goTo('forest', { from: 'graveyard' });
  }

  _panel(html){
    const el = document.createElement('div');
    el.className = 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  update(dt){
    const t = performance.now() * 0.001;
    this.ghost.position.y = this.ghostBaseY + Math.sin(t * 1.8) * 0.15;
    this.ghost.rotation.y = Math.sin(t * 0.5) * 0.12;
    this.ghostLight.intensity = 0.4 + 0.15 * Math.sin(t * 3);

    this.bread.update(dt);

    const playerPos = this.bread.root.position;
    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      const d = h.pos.distanceTo(playerPos);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => near.action());
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    const camTarget = new THREE.Vector3(playerPos.x, 1.6, playerPos.z);
    this.camera.position.lerp(new THREE.Vector3(
      playerPos.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      1.8 + this.camDist * Math.sin(this.polar),
      playerPos.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    ), 0.08);
    this.camera.lookAt(camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
